/**
 * AdvertiserSubUsersPage — manage sub-users for an advertiser workspace.
 *
 * Flow:
 *   - Enter an advertiser_id + optional status filter → list sub-users.
 *   - Invite form creates a new sub-user (email + role + optional name).
 *     Response includes an invite_token shown inline (one-time reveal).
 *   - Per-row actions: change role (select) and revoke (button).
 *
 * Brand: green only. Light theme. Zinc borders. 6–8px radius.
 */

import { useMemo, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import {
	useAdvertiserSubUsersChangeRole,
	useAdvertiserSubUsersInvite,
	useAdvertiserSubUsersList,
	useAdvertiserSubUsersRevoke,
} from "./hooks";
import {
	type InviteSubUserResponse,
	type SubUserRole,
	type SubUserStatus,
	type SubUserView,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — green brand, zinc borders, 6–8px rounding.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = `${INPUT} pr-8`;
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_BASE =
	"inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

const ROLES: SubUserRole[] = ["admin", "editor", "viewer"];
const STATUSES: readonly ("all" | SubUserStatus)[] = [
	"all",
	"invited",
	"active",
	"revoked",
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface AdvertiserSubUsersPageProps {
	advertiserId?: string;
}

export function AdvertiserSubUsersPage(
	props: AdvertiserSubUsersPageProps = {},
) {
	const [advertiserId, setAdvertiserId] = useState(props.advertiserId ?? "");
	const [status, setStatus] = useState<"all" | SubUserStatus>("all");

	const listQuery = useAdvertiserSubUsersList(
		advertiserId.trim(),
		status === "all" ? undefined : status,
	);
	const inviteMut = useAdvertiserSubUsersInvite();
	const revokeMut = useAdvertiserSubUsersRevoke();
	const roleMut = useAdvertiserSubUsersChangeRole();

	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [role, setRole] = useState<SubUserRole>("viewer");
	const [lastInvite, setLastInvite] = useState<InviteSubUserResponse | null>(
		null,
	);

	const items: SubUserView[] = useMemo(
		() => listQuery.data?.items ?? [],
		[listQuery.data],
	);

	const handleInvite = (e: FormEvent) => {
		e.preventDefault();
		const trimmedAdv = advertiserId.trim();
		if (!trimmedAdv || !email.trim()) return;
		inviteMut.mutate(
			{
				advertiser_id: trimmedAdv,
				email: email.trim(),
				name: name.trim() || undefined,
				role,
			},
			{
				onSuccess: (data) => {
					setLastInvite(data);
					setEmail("");
					setName("");
				},
			},
		);
	};

	const pendingAdvertiser = advertiserId.trim().length === 0;

	return (
		<main
			data-testid="advertiser-sub-users-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Breadcrumb />
			<Heading />

			{/* Filter / context bar */}
			<section
				className={`${CARD} flex flex-col gap-3 p-4 sm:flex-row sm:items-end`}
				aria-label="Advertiser filter"
			>
				<div className="flex flex-1 flex-col gap-1.5">
					<label htmlFor="asu-adv-id" className={LABEL}>
						Advertiser ID
					</label>
					<input
						id="asu-adv-id"
						type="text"
						className={INPUT}
						placeholder="adv_1234"
						value={advertiserId}
						onChange={(e) => setAdvertiserId(e.currentTarget.value)}
						data-testid="advertiser-sub-users-advertiser-input"
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="asu-status" className={LABEL}>
						Status
					</label>
					<select
						id="asu-status"
						className={`${SELECT} min-w-[9rem]`}
						value={status}
						onChange={(e) =>
							setStatus(e.currentTarget.value as "all" | SubUserStatus)
						}
						data-testid="advertiser-sub-users-status-filter"
					>
						{STATUSES.map((s) => (
							<option key={s} value={s}>
								{s === "all" ? "All statuses" : s}
							</option>
						))}
					</select>
				</div>
			</section>

			{/* Invite form */}
			<section
				className={`${CARD} p-5`}
				aria-label="Invite a new sub-user"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Invite a sub-user
				</h2>
				<p className={`mt-1 ${HINT}`}>
					Sends an invite. The one-time token is shown below — forward it
					to the invitee.
				</p>
				<form
					onSubmit={handleInvite}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[2fr_2fr_1fr_auto] sm:items-end"
					data-testid="advertiser-sub-users-invite-form"
				>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="asu-email" className={LABEL}>
							Email
						</label>
						<input
							id="asu-email"
							type="email"
							className={INPUT}
							placeholder="user@example.com"
							value={email}
							onChange={(e) => setEmail(e.currentTarget.value)}
							disabled={inviteMut.isPending}
							data-testid="advertiser-sub-users-email-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="asu-name" className={LABEL}>
							Name <span className={HINT}>(optional)</span>
						</label>
						<input
							id="asu-name"
							type="text"
							className={INPUT}
							placeholder="Full name"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={inviteMut.isPending}
							data-testid="advertiser-sub-users-name-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="asu-role" className={LABEL}>
							Role
						</label>
						<select
							id="asu-role"
							className={SELECT}
							value={role}
							onChange={(e) =>
								setRole(e.currentTarget.value as SubUserRole)
							}
							disabled={inviteMut.isPending}
							data-testid="advertiser-sub-users-role-input"
						>
							{ROLES.map((r) => (
								<option key={r} value={r}>
									{r}
								</option>
							))}
						</select>
					</div>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={
							inviteMut.isPending ||
							pendingAdvertiser ||
							!email.trim()
						}
						data-testid="advertiser-sub-users-invite-submit"
					>
						{inviteMut.isPending ? "Sending." : "Invite"}
					</button>
				</form>
				{inviteMut.error ? (
					<p className={`mt-3 ${ERROR_TEXT}`} role="alert">
						{inviteMut.error.message}
					</p>
				) : null}
				{lastInvite ? (
					<div
						className="mt-4 rounded-[6px] border border-green-200 bg-green-50 p-3 text-xs text-green-900"
						data-testid="advertiser-sub-users-invite-token"
					>
						<p className="font-semibold">
							Invite created for {lastInvite.sub_user.email}
						</p>
						<p className="mt-1 break-all font-mono text-[11px] text-green-800">
							Token: {lastInvite.invite_token}
						</p>
						<p className="mt-1 text-[11px] text-green-700">
							This token is only returned once. Copy it now.
						</p>
					</div>
				) : null}
			</section>

			{/* List */}
			<section className={`${CARD} p-5`} aria-label="Sub-users list">
				<div className="flex items-center justify-between gap-3">
					<h2 className="text-base font-semibold text-zinc-900">
						Sub-users
					</h2>
					{listQuery.data ? (
						<span className={HINT}>
							{listQuery.data.count} total
						</span>
					) : null}
				</div>

				{pendingAdvertiser ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-sub-users-awaiting-advertiser"
					>
						Enter an advertiser ID above to load sub-users.
					</p>
				) : listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-sub-users-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className={`mt-3 ${ERROR_TEXT}`} role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-sub-users-empty"
					>
						No sub-users for this advertiser.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="min-w-full divide-y divide-zinc-200 text-sm"
							data-testid="advertiser-sub-users-list"
						>
							<thead className="bg-zinc-50 text-xs uppercase tracking-[0.08em] text-zinc-500">
								<tr>
									<th
										scope="col"
										className="px-3 py-2 text-left"
									>
										Email
									</th>
									<th
										scope="col"
										className="px-3 py-2 text-left"
									>
										Name
									</th>
									<th
										scope="col"
										className="px-3 py-2 text-left"
									>
										Role
									</th>
									<th
										scope="col"
										className="px-3 py-2 text-left"
									>
										Status
									</th>
									<th
										scope="col"
										className="px-3 py-2 text-right"
									>
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => (
									<SubUserRowView
										key={row.id}
										row={row}
										onChangeRole={(r) =>
											roleMut.mutate({
												id: row.id,
												role: r,
											})
										}
										onRevoke={() =>
											revokeMut.mutate(row.id)
										}
										rolePending={
											roleMut.isPending &&
											roleMut.variables?.id === row.id
										}
										revokePending={
											revokeMut.isPending &&
											revokeMut.variables === row.id
										}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
				{(roleMut.error || revokeMut.error) && (
					<p className={`mt-3 ${ERROR_TEXT}`} role="alert">
						{roleMut.error?.message || revokeMut.error?.message}
					</p>
				)}
			</section>
		</main>
	);
}

export default AdvertiserSubUsersPage;

// ---------------------------------------------------------------------------
// Fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Advertiser sub-users</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Advertisers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Sub-users
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Add teammates to an advertiser workspace, pick a role, and revoke
				access when people roll off. Invites arrive via a one-time token.
			</p>
		</header>
	);
}

interface RowProps {
	row: SubUserView;
	onChangeRole: (role: SubUserRole) => void;
	onRevoke: () => void;
	rolePending: boolean;
	revokePending: boolean;
}

function SubUserRowView(props: RowProps) {
	const { row } = props;
	const revoked = row.status === "revoked";
	return (
		<tr data-testid={`advertiser-sub-users-row-${row.id}`}>
			<td className="px-3 py-2 font-mono text-xs text-zinc-800">
				{row.email}
			</td>
			<td className="px-3 py-2 text-zinc-700">{row.name || "—"}</td>
			<td className="px-3 py-2">
				<select
					className={`${SELECT} min-w-[7rem]`}
					value={row.role}
					onChange={(e) =>
						props.onChangeRole(
							e.currentTarget.value as SubUserRole,
						)
					}
					disabled={revoked || props.rolePending}
					aria-label={`Role for ${row.email}`}
					data-testid={`advertiser-sub-users-role-${row.id}`}
				>
					{ROLES.map((r) => (
						<option key={r} value={r}>
							{r}
						</option>
					))}
				</select>
			</td>
			<td className="px-3 py-2">
				<StatusPill status={row.status} />
			</td>
			<td className="px-3 py-2 text-right">
				{revoked ? (
					<button
						type="button"
						className={BTN_GHOST}
						disabled
						aria-disabled="true"
					>
						Revoked
					</button>
				) : (
					<button
						type="button"
						className={BTN_DANGER}
						onClick={props.onRevoke}
						disabled={props.revokePending}
						data-testid={`advertiser-sub-users-revoke-${row.id}`}
					>
						{props.revokePending ? "Revoking." : "Revoke"}
					</button>
				)}
			</td>
		</tr>
	);
}

function StatusPill(props: { status: SubUserStatus }) {
	const cls =
		props.status === "active"
			? "border-green-200 bg-green-50 text-green-800"
			: props.status === "invited"
				? "border-zinc-200 bg-zinc-50 text-zinc-700"
				: "border-red-200 bg-red-50 text-red-700";
	return <span className={`${PILL_BASE} ${cls}`}>{props.status}</span>;
}
